import { httpService } from './httpService';
import { HoursState, Complaint, ComplaintLeaderboardEntry } from '../types';

export const hrDashboardService = {
  getHoursState: async (): Promise<HoursState> => {
    const response = await httpService.get<HoursState>('/users/@me/hr-dashboard/state');
    return response.data;
  },

  getUserHoursState: async (userId: number): Promise<HoursState> => {
    const response = await httpService.get<HoursState>(`/users/${userId}/hr-dashboard/state`);
    return response.data;
  },

  saveHoursState: async (state: HoursState): Promise<HoursState> => {
    const response = await httpService.put<HoursState>('/users/@me/hr-dashboard/state', state);
    return response.data;
  },

  saveUserHoursState: async (userId: number, state: HoursState): Promise<HoursState> => {
    const response = await httpService.put<HoursState>(`/users/${userId}/hr-dashboard/state`, state);
    return response.data;
  },

  sendSlackReminders: async (month: string, userIds?: number[], message?: string): Promise<any> => {
    const body: any = { month };
    if (userIds && userIds.length > 0) body.userIds = userIds;
    if (message) body.message = message;
    const response = await httpService.post('/hr/reminders/slack', body);
    return response.data;
  },
  getReminderCandidates: async (month: string): Promise<any[]> => {
    const response = await httpService.get<any[]>(`/hr/reminders/candidates?month=${encodeURIComponent(month)}`);
    return response.data;
  },

  getSettings: async (): Promise<{ admins: number[]; fillers: number[] }> => {
    const response = await httpService.get<{ admins: number[]; fillers: number[] }>('/hr/settings');
    return response.data;
  },

  updateSettings: async (settings: { admins?: number[]; fillers?: number[] }): Promise<{ admins: number[]; fillers: number[] }> => {
    const response = await httpService.put<{ admins: number[]; fillers: number[] }>('/hr/settings', settings);
    return response.data;
  },

  searchUsers: async (query: string): Promise<Array<{ userId: number; firstName: string | null; lastName: string | null; primaryEmail: string | null }>> => {
    const response = await httpService.get<Array<{ userId: number; firstName: string | null; lastName: string | null; primaryEmail: string | null }>>(
      `/hr/users/search?query=${encodeURIComponent(query)}`
    );
    return response.data;
  },

  getUsersByIds: async (userIds: number[]): Promise<Array<{ userId: number; firstName: string | null; lastName: string | null; primaryEmail: string | null }>> => {
    if (userIds.length === 0) return [];
    const ids = userIds.join(',');
    const response = await httpService.get<Array<{ userId: number; firstName: string | null; lastName: string | null; primaryEmail: string | null }>>(
      `/hr/users/by-ids?ids=${encodeURIComponent(ids)}`
    );
    return response.data;
  },

  getAllEntriesForMonth: async (month: string): Promise<Array<{ userId: number; userName: string; hours: number; notes: string; updatedAt: string }>> => {
    const response = await httpService.get<Array<{ userId: number; userName: string; hours: number; notes: string; updatedAt: string }>>(
      `/hr/entries?month=${encodeURIComponent(month)}`
    );
    return response.data;
  },

  searchSlackUsers: async (query: string): Promise<Array<{ id: string; name: string; displayName: string; email: string | null; avatar: string | null }>> => {
    const response = await httpService.get<Array<{ id: string; name: string; displayName: string; email: string | null; avatar: string | null }>>(
      `/hr/slack-users/search?query=${encodeURIComponent(query)}`
    );
    return response.data;
  },

  getSlackUsersByIds: async (slackUserIds: string[]): Promise<Array<{ id: string; name: string; displayName: string; email: string | null; avatar: string | null }>> => {
    if (slackUserIds.length === 0) return [];
    const ids = slackUserIds.join(',');
    const response = await httpService.get<Array<{ id: string; name: string; displayName: string; email: string | null; avatar: string | null }>>(
      `/hr/slack-users/by-ids?ids=${encodeURIComponent(ids)}`
    );
    return response.data;
  },

  createComplaint: async (payload: { aboutName: string; incidentAt: string; explanation: string }): Promise<Complaint> => {
    const response = await httpService.post<Complaint>('/hr/complaints', payload);
    return response.data;
  },

  getComplaints: async (): Promise<Complaint[]> => {
    const response = await httpService.get<Complaint[]>('/hr/complaints');
    return response.data;
  },

  getComplaintLeaderboard: async (): Promise<ComplaintLeaderboardEntry[]> => {
    const response = await httpService.get<ComplaintLeaderboardEntry[]>('/hr/complaints/leaderboard');
    return response.data;
  },

  getComplaintNames: async (): Promise<string[]> => {
    const response = await httpService.get<string[]>('/hr/complaints/names');
    return response.data;
  },

  getSlackMappings: async (): Promise<Record<number, string>> => {
    const response = await httpService.get<{ mappings: Record<number, string> }>('/hr/slack-mappings');
    return response.data.mappings;
  },

  updateSlackMappings: async (mappings: Record<number, string>): Promise<Record<number, string>> => {
    const response = await httpService.put<{ mappings: Record<number, string> }>('/hr/slack-mappings', { mappings });
    return response.data.mappings;
  },
};